import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
 ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  S3FileUploadInput,
  S3FileUploadRequest,
  S3FileUploadService,
} from './s3-file-upload.service';
import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
export type { S3FileUploadInput } from './s3-file-upload.service';

@Component({
    selector: 'gtw-s3-file-upload',
    templateUrl: './s3-file-upload.component.html',
    styleUrls: ['./s3-file-upload.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class S3FileUpload implements OnInit, OnDestroy {
  rootFolder: string = 'gtw';
  isNewFile: boolean = false;
  private readonly DEFAULT_TITLE = 'Upload file to S3';
  title: string = this.DEFAULT_TITLE;
  /** Orange header + themed body when not embedded (e.g. S3 Management modal). */
  currentTheme = 'theme-black';

  private readonly destroy$ = new Subject<void>();
  /** True once `row-data` (modal / CE host) successfully drove `init`. */
  private initFromRowData = false;
  private hostAdminBaseUrl: string | null = null;
  private rowDataRaw: string | null = null;

  get showModalChrome(): boolean {
    return !this.s3FileUploadInput?.hidePath;
  }

  /** Inline upload inside efile review-attachments (`hidePath: true`). */
  get isEfileEmbeddedUpload(): boolean {
    return !!this.s3FileUploadInput?.hidePath && !this.showModalChrome;
  }

  @Output('on-s3-file-upload')
  onFileUpload: EventEmitter<string> = new EventEmitter<string>();

  s3FileUploadInput!: S3FileUploadInput;

  uploadAPIEndPoint: string = '';
  breadCrumbLinks: string[] = [];
  newFolder: string = '';

  description: string = '';
  private readonly REPLACE_TEXT =
    'Replace an existing file to the path shown above';
  private readonly NEW_UPLOAD_TEXT =
    'Upload a new file to the path shown above';
  /** Efile review-attachments tab hides the path — do not reference it in copy. */
  private readonly EFILE_EMBEDDED_UPLOAD_TEXT =
    'Drag and drop your file here, or choose your file';

  s3FileUploadRequest!: S3FileUploadRequest;
  disabledText: string | null = null;
  isClientSpecificFile: boolean = true;
  uploadInProgress = false;
  selectedFile: File | null = null;

  /**
   * Native file input ref — used by `clearSelectedFile()` to reset
   * `<input type="file">`'s value so the user can re-select the same file.
   */
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  constructor(
    private s3UploadService: S3FileUploadService,
    private appService: AppService,
    private gtwSnackbarService: GtwSnackbarService
  ) {
    this.s3FileUploadInput = this.s3UploadService.resolveS3FileUploadInput({});
  }

  @Input('base-urls')
  set baseUrlsInput(json: string | undefined | null) {
    if (json == null || String(json).trim() === '') {
      return;
    }
    try {
      const o = safeJsonInput<any>(json, null, 's3-file-upload.parse1') as { admin?: string };
      this.hostAdminBaseUrl =
        o?.admin != null && String(o.admin).trim() !== ''
          ? String(o.admin)
          : null;
      this.tryInitFromRowData();
    } catch {
      console.log('base-urls input not yet stable.');
    }
  }

  @Input('row-data')
  set rowDataInput(value: string | undefined | null) {
    if (value == null || String(value).trim() === '') {
      return;
    }
    // Drop any stale embedded context (e.g. hidePath:true from folder manager)
    // so this instance does not init from BehaviorSubject before row-data applies.
    this.s3UploadService.publishUploadViewContext(null);
    this.rowDataRaw = value;
    this.tryInitFromRowData();
  }

  ngOnInit(): void {
    this.syncCurrentTheme();
    // Defer so @Input('row-data') / @Input('base-urls') from CE host run first;
    // avoids consuming a stale publishUploadViewContext value on first subscribe.
    queueMicrotask(() => {
      this.s3UploadService
        .getUploadViewContextStream()
        .pipe(
          filter(
            (ctx) =>
              !this.initFromRowData &&
              ctx != null &&
              Object.keys(ctx as object).length > 0
          ),
          takeUntil(this.destroy$)
        )
        .subscribe((ctx) => {
          this.init(
            this.s3UploadService.resolveS3FileUploadInput(
              ctx as Partial<S3FileUploadInput>
            )
          );
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.initFromRowData) {
      this.s3UploadService.publishUploadViewContext(null);
    }
  }

  private tryInitFromRowData(): void {
    if (!this.rowDataRaw) {
      return;
    }
    const row = this.parseRowDataAttr(this.rowDataRaw);
    if (row == null) {
      return;
    }
    this.initFromRowData = true;
    const partial = this.s3UploadService.buildPartialFromS3ManagementRowData(
      row,
      this.hostAdminBaseUrl
    );
    this.init(this.s3UploadService.resolveS3FileUploadInput(partial));
  }

  private parseRowDataAttr(
    raw: string
  ): Record<string, unknown> | null {
    try {
      return safeJsonInput<any>(raw, null, 's3-file-upload.parse2') as Record<string, unknown>;
    } catch {
      try {
        return safeJsonInput<any>(
          decodeURIComponent(raw)
        , null, 's3-file-upload.parse3') as Record<string, unknown>;
      } catch {
        return null;
      }
    }
  }

  private init(raw: Partial<S3FileUploadInput> | S3FileUploadInput) {
    this.s3FileUploadInput = this.s3UploadService.resolveS3FileUploadInput(raw);
    const s3FileUploadInput = this.s3FileUploadInput;

    this.syncCurrentTheme();

    this.title = s3FileUploadInput.title || this.DEFAULT_TITLE;

    this.description = s3FileUploadInput.fileKey
      ? s3FileUploadInput.hidePath
        ? 'Replace the existing file'
        : this.REPLACE_TEXT
      : s3FileUploadInput.hidePath
        ? this.EFILE_EMBEDDED_UPLOAD_TEXT
        : this.NEW_UPLOAD_TEXT;

    this.isNewFile = !s3FileUploadInput.fileKey;
    this.isClientSpecificFile = !s3FileUploadInput.onlyCore;

    this.rootFolder =
      s3FileUploadInput.path?.split('/').filter((e) => e !== '')[0] ||
      this.rootFolder;

    this.breadCrumbLinks = s3FileUploadInput.path
      ? s3FileUploadInput.path
          .split('/')
          .filter(
            (e: string) =>
              e !== '' &&
              e !== this.rootFolder &&
              e !== this.s3FileUploadInput.fileKey?.toString()
          )
      : [];

    this.uploadAPIEndPoint =
      s3FileUploadInput.baseUrl + '/uploadOrReplaceFileinS3';

    this.s3FileUploadRequest = {
      path: this.breadCrumbLinks.join('/'),
      fileKey: s3FileUploadInput.fileKey || '',
      rootFolder: this.rootFolder,
      clientKey: s3FileUploadInput.clientKey,
    };

    if (!this.isClientSpecificFile || s3FileUploadInput.clientKey == null) {
      delete this.s3FileUploadRequest.clientKey;
    }

    this.checkValidation();
  }

  private syncCurrentTheme(): void {
    this.currentTheme =
      this.appService.USER_SETTINGS?.user?.selected_theme || 'theme-black';
  }

  closeModal(): void {
    const host = document.querySelector('gtw-s3-file-upload-ce');
    host?.dispatchEvent(
      new CustomEvent('close-modal', { bubbles: true, composed: true })
    );
  }

  onAddFolderClick() {
    const folderName = (this.newFolder || '').trim();
    if (!folderName) {
      return;
    }

    this.breadCrumbLinks.push(folderName);
    this.newFolder = '';

    this.s3FileUploadRequest.path = this.breadCrumbLinks.join('/');

    this.checkValidation();
  }

  private checkValidation() {
    // Efile review-attachments (hidePath) uses a fixed path under /gtw/binaries/…/
    // review-attachments/. Server read_only_s3_paths lists /gtw/binaries for the
    // free-form S3 browser only — must not block this flow (legacy passed '...').
    if (this.s3FileUploadInput.hidePath) {
      this.disabledText = null;
      return;
    }

    const clientKey = this.s3FileUploadInput.clientKey?.toString().trim() || '';
    const pathSegments = (this.s3FileUploadRequest.path || '')
      .split('/')
      .map((segment) => segment.trim())
      .filter((segment) => segment !== '');

    if (
      this.s3FileUploadInput.readOnlyS3Paths
        .split(',')
        .map((path) => path.trim())
        .filter((path) => path !== '')
        .some((path) =>
          ('/' + this.rootFolder + '/' + this.s3FileUploadRequest.path).includes(
            path
          )
        )
    ) {
      this.disabledText =
        'This is a restricted folder. Please use some other path.';
    } else if (pathSegments.length === 0) {
      this.disabledText = 'Please enter a subfolder to upload the file';
    } else if (
      clientKey &&
      !pathSegments.includes(clientKey) &&
      this.rootFolder !== clientKey &&
      this.isClientSpecificFile &&
      this.isNewFile
    ) {
      this.disabledText = 'Please include client key in the path';
    } else {
      this.disabledText = null;
    }
  }

  onClientSpecificFlagChanged(event: unknown) {
    const checked = this.resolveApCheckboxCheckedFromEvent(event);
    this.isClientSpecificFile = checked;
    if (!checked || this.s3FileUploadInput.clientKey == null) {
      delete this.s3FileUploadRequest.clientKey;
    } else {
      this.s3FileUploadRequest.clientKey = this.s3FileUploadInput.clientKey;
    }
    this.checkValidation();
  }

  /** Matches `ap-checkbox` `(onChange)` payloads used elsewhere (e.g. user-details). */
  private resolveApCheckboxCheckedFromEvent(event: unknown): boolean {
    if (typeof event === 'boolean') {
      return event;
    }
    if (event != null && typeof event === 'object') {
      const o = event as { checkboxState?: boolean };
      if (typeof o.checkboxState === 'boolean') {
        return o.checkboxState;
      }
    }
    return this.isClientSpecificFile;
  }

  /**
   * Native `<input type="file">` `(change)` handler — mirrors the pattern
   * used by `xml-consol-upload`, `pdf-upload`, `upload-irs-schema`, and
   * `upload-files`. The previous `<ap-fileupload>` path routed uploads
   * through `ng2-file-upload`'s raw XHR (`_xhrTransport`), which bypasses
   * Angular's `AuthInterceptor` and 401'd the `/admin/` gateway.
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files && input.files.length > 0 ? input.files[0] : null;
    this.selectedFile = file;
  }

  /**
   * `dragover` on the dropzone label.
   *
   * `preventDefault()` is mandatory — without it the browser falls back to its
   * default "open file in this tab / navigate to file URL" behavior on drop.
   * We also suppress dragover while disabled / uploading so the cursor reflects
   * the state.
   */
  onDragOver(event: DragEvent): void {
    if (this.disabledText || this.uploadInProgress) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * `drop` on the dropzone label. Native `<input type="file">` would handle a
   * drop on itself, but the label wraps a wider zone — we forward the dropped
   * file straight into `selectedFile` (single-file flow) and also sync the
   * input's `files` so a downstream `change` doesn't blank it out.
   */
  onDrop(event: DragEvent): void {
    if (this.disabledText || this.uploadInProgress) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const dropped = event.dataTransfer?.files;
    if (!dropped || dropped.length === 0) {
      return;
    }
    this.selectedFile = dropped[0];
    if (this.fileInput?.nativeElement) {
      try {
        // Sync the native input's FileList so a subsequent `change` / form
        // submission sees the same file. Falls back silently if the browser
        // does not support `DataTransfer` assignment (older Safari).
        const dt = new DataTransfer();
        dt.items.add(dropped[0]);
        this.fileInput.nativeElement.files = dt.files;
      } catch {
        // Older browser fallback — the upload still works because
        // `uploadSelectedFile()` reads from `this.selectedFile`, not the input.
      }
    }
  }

  /** Clear the selected file (called by the inline "x" button next to filename). */
  clearSelectedFile(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedFile = null;
    if (this.fileInput?.nativeElement) {
      // Reset native value so the user can re-select the same file.
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
   * Manual "Upload" button handler — POSTs the selected file through
   * `HttpClient` (which goes through `AuthInterceptor`), the same pattern
   * `efile/components/upload-files`, `efile/upload-irs-schema`, and
   * `efile/components/xml-consol-upload` use.
   */
  uploadSelectedFile(): void {
    if (this.disabledText || this.uploadInProgress || !this.selectedFile) {
      return;
    }

    const file = this.selectedFile;

    // const authorization = sessionStorage.getItem('Authorization') || '';
    // if (!authorization) {
    //   this.gtwSnackbarService.openSnackBar(
    //     'Upload unauthorized. Please sign in again and retry.',
    //     'gtw-snackbar--red'
    //   );
    //   return;
    // }

    this.uploadInProgress = true;
    this.s3UploadService
      .uploadFile(this.uploadAPIEndPoint, file, this.s3FileUploadRequest).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: unknown) => {
          this.uploadInProgress = false;
          this.clearSelectedFile();

          const res = response as {
            callSuccess?: string | number;
            errorMessage?: string;
          };
          const ok =
            res?.callSuccess === '1' ||
            res?.callSuccess === 1 ||
            res?.callSuccess === undefined;

          if (!ok) {
            this.gtwSnackbarService.openSnackBar(
              res?.errorMessage || 'Upload failed. Please try again.',
              'gtw-snackbar--red'
            );
            return;
          }

          const payload =
            typeof response === 'string'
              ? response
              : JSON.stringify(response ?? {});

          if (!payload || payload === 'null') {
            return;
          }

          if (!this.isNewFile) {
            this.gtwSnackbarService.openSnackBar(
              'File replaced successfully.',
              'gtw-snackbar--green'
            );
          } else {
            this.gtwSnackbarService.openSnackBar(
              'File uploaded successfully.',
              'gtw-snackbar--green'
            );
          }
          this.onFileUpload.emit(payload);
        },
        error: (error: { status?: number }) => {
          this.uploadInProgress = false;
          console.error('Upload failed:', error);
          const message =
            error?.status === 401
              ? 'Upload unauthorized. Please sign in again and retry.'
              : 'Error occurred while uploading the file.';
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--red');
        },
      });
  }

  downloadFileFromS3() {
    this.s3UploadService
      .downloadFile(
        this.s3FileUploadInput.fileKey,
        this.s3FileUploadInput.clientKey,
        this.s3FileUploadInput.baseUrl
      ).pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while downloading the file.',
            'gtw-snackbar--red'
          );
        },
      });
  }
}
